<script setup lang="ts">
import { computed, ref } from 'vue';
import ReportSection from '@/_core/entities/report/ui/ReportSection/ReportSection.vue';
import Accordion from '@/_core/shared/ui/Accordion/Accordion.vue';
import TrialOverlay from '@/_core/entities/vulnerability/ui/TrialOverlay/TrialOverlay.vue';
import ExpandToggleButton from '@/_core/shared/ui/ExpandToggleButton/ExpandToggleButton.vue';

const props = withDefaults(
  defineProps<{
    hasLeft?: boolean;
    hasRight?: boolean;
    leftTrialTitle?: string;
    rightTrialTitle?: string;
    isLeftContentUnlocked?: boolean;
    isRightContentUnlocked?: boolean;
  }>(),
  {
    hasLeft: false,
    hasRight: false,
    leftTrialTitle: '',
    rightTrialTitle: '',
    isLeftContentUnlocked: true,
    isRightContentUnlocked: true,
  }
);

// Shared expand/collapse state for the xl+ single-panel layout.
const isSharedExpanded = ref(false);
const isLeftOverflowing = ref(false);
const isRightOverflowing = ref(false);
const isAnyOverflowing = computed(() => isLeftOverflowing.value || isRightOverflowing.value);

// Use a 2-col grid only when both cards are present so the row keeps equal-height
// cells regardless of content length. Fall back to a single column otherwise.
const desktopGridColsClass = computed(() =>
  props.hasLeft && props.hasRight ? 'tw:grid-cols-2' : 'tw:grid-cols-1'
);
</script>

<template>
  <div v-if="hasLeft || hasRight">
    <div class="tw:hidden tw:xl:block">
      <ReportSection>
        <div class="tw:grid tw:gap-8" :class="desktopGridColsClass">
          <TrialOverlay
            v-if="hasLeft"
            :title="leftTrialTitle"
            :isContentUnlocked="isLeftContentUnlocked"
            class="tw:min-w-0"
          >
            <Accordion
              :value="isSharedExpanded"
              hide-toggle
              @input="isSharedExpanded = $event"
              @overflow-change="isLeftOverflowing = $event"
            >
              <slot name="left-content" />
              <template v-if="$slots['left-footer']" #footer>
                <slot name="left-footer" />
              </template>
            </Accordion>
          </TrialOverlay>

          <TrialOverlay
            v-if="hasRight"
            :title="rightTrialTitle"
            :isContentUnlocked="isRightContentUnlocked"
            class="tw:min-w-0"
          >
            <Accordion
              :value="isSharedExpanded"
              hide-toggle
              @input="isSharedExpanded = $event"
              @overflow-change="isRightOverflowing = $event"
            >
              <slot name="right-content" />
              <template v-if="$slots['right-footer']" #footer>
                <slot name="right-footer" />
              </template>
            </Accordion>
          </TrialOverlay>
        </div>

        <div v-if="isAnyOverflowing" class="tw:mt-4 tw:flex tw:justify-start">
          <ExpandToggleButton :value="isSharedExpanded" @input="isSharedExpanded = $event" />
        </div>
      </ReportSection>
    </div>

    <div class="tw:flex tw:flex-col tw:gap-2 tw:lg:gap-4 tw:xl:hidden">
      <TrialOverlay
        v-if="hasLeft"
        :title="leftTrialTitle"
        :isContentUnlocked="isLeftContentUnlocked"
      >
        <ReportSection>
          <Accordion>
            <slot name="left-content" />
            <template v-if="$slots['left-footer']" #footer>
              <slot name="left-footer" />
            </template>
          </Accordion>
        </ReportSection>
      </TrialOverlay>

      <TrialOverlay
        v-if="hasRight"
        :title="rightTrialTitle"
        :isContentUnlocked="isRightContentUnlocked"
      >
        <ReportSection>
          <Accordion>
            <slot name="right-content" />
            <template v-if="$slots['right-footer']" #footer>
              <slot name="right-footer" />
            </template>
          </Accordion>
        </ReportSection>
      </TrialOverlay>
    </div>
  </div>
</template>
